import React from 'react';
import { Text } from 'recharts';
import { AXIS_LABEL_STYLE } from './chartTheme';

// Headroom a chart must reserve above its plot area for a ReferenceLineTopLabel. Recharts'
// built-in `position: 'top'` label sits above the plot and is centered on the line, so with
// the usual 8px top margin its glyphs were clipped, and a line near either edge of the plot
// also lost half its text sideways. Any chart that uses this label sets margin.top to this.
export const REFERENCE_LABEL_TOP_MARGIN = 24;

/**
 * Choose which side of a vertical reference line its label should run toward, so the text
 * always extends into the plot interior rather than past the chart's left/right edge.
 * `lo`/`hi` are the visible X-axis bounds (the zoomed domain when zoomed).
 */
export function referenceLabelAnchor(x, lo, hi) {
  if (!Number.isFinite(x) || !Number.isFinite(lo) || !Number.isFinite(hi) || hi <= lo) return 'middle';
  const frac = (x - lo) / (hi - lo);
  if (frac > 0.6) return 'end';
  if (frac < 0.4) return 'start';
  return 'middle';
}

/**
 * Resolve a useChartZoom xDomain (which may contain 'dataMin'/'dataMax') to numeric bounds
 * from the chart's rows, then pick the label anchor for a line at `x`.
 */
export function referenceLabelAnchorFor(x, xDomain, rows, dataKey) {
  const xs = (rows || []).map((r) => Number(r[dataKey])).filter(Number.isFinite);
  if (!xs.length) return 'middle';
  const resolve = (d, fallback) => (typeof d === 'number' ? d : fallback);
  const lo = resolve(xDomain?.[0], Math.min(...xs));
  const hi = resolve(xDomain?.[1], Math.max(...xs));
  return referenceLabelAnchor(x, lo, hi);
}

/**
 * Label content for a vertical Recharts <ReferenceLine>, drawn just above the plot area.
 * Pass as `label={<ReferenceLineTopLabel text="Max RPM" color="#dc2626" anchor="end" />}`.
 * Uses Recharts' <Text> (not a raw <text>) so the dev-only visual-edits Babel plugin does
 * not wrap the dynamic child in an HTML <span>, which is invalid inside SVG.
 */
const ReferenceLineTopLabel = ({ viewBox, text, color, anchor = 'middle' }) => {
  if (!viewBox) return null;
  const dx = anchor === 'start' ? 4 : anchor === 'end' ? -4 : 0;
  return (
    <Text
      x={viewBox.x + dx}
      y={viewBox.y - 6}
      textAnchor={anchor}
      verticalAnchor="end"
      fill={color || AXIS_LABEL_STYLE.fill}
      fontSize={11}
      fontFamily={AXIS_LABEL_STYLE.fontFamily}
    >
      {text}
    </Text>
  );
};

export default ReferenceLineTopLabel;
